// AssignCustomerModal — picker leve pra atribuir customer a um recurso
// (form, coleção, etc) sem precisar abrir o builder/editor completo.
//
// 2026-05-19: atalho descoberto pela FormCard menu "Atribuir a cliente".
// Reusável pra outras listas no futuro (CollectionCard, AppCard).

import { useState, useEffect } from 'react';
import { X, Loader2, Plus } from 'lucide-react';
import { customersApi, type CustomerDTO } from '../lib/api';
import { CustomerModal } from './CustomerModal';

interface Props {
  /** Nome do recurso pra mostrar no header (ex: "Cadastro Cliente"). */
  resourceLabel: string;
  /** Tipo do recurso pra label do botão (form, coleção, etc). Default 'form'. */
  resourceKind?: string;
  /** Customer atual (null = sem cliente). */
  currentCustomerId: string | null;
  /** Chamado com novo customerId (ou null pra desassociar). */
  onSave: (customerId: string | null) => Promise<void>;
  onClose: () => void;
}

export function AssignCustomerModal({
  resourceLabel,
  resourceKind = 'form',
  currentCustomerId,
  onSave,
  onClose,
}: Props) {
  const [customers, setCustomers] = useState<CustomerDTO[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(currentCustomerId);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creatingNew, setCreatingNew] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const r = await customersApi.list();
      setCustomers(r.items);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  // ESC fecha
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleSave() {
    setSaving(true);
    setErr(null);
    try {
      await onSave(selectedId);
      onClose();
    } catch (e) {
      setErr((e as Error).message);
      setSaving(false);
    }
  }

  const dirty = selectedId !== currentCustomerId;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
        <div
          className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div>
              <h2 className="text-base font-bold text-gray-900">Atribuir a cliente</h2>
              <p className="text-xs text-gray-500 mt-0.5 truncate max-w-xs">
                {resourceKind === 'form' ? '📝' : '📋'} {resourceLabel}
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1">
              <X size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="p-5 space-y-3">
            <p className="text-xs text-gray-500">
              Cliente é uma <strong>tag visual</strong> de organização. Não trava permissão —
              {' '}o {resourceKind} continua acessível normalmente.
            </p>

            {loading ? (
              <div className="flex items-center gap-2 text-sm text-gray-400 py-4 justify-center">
                <Loader2 size={14} className="animate-spin" /> Carregando clientes…
              </div>
            ) : (
              <div className="space-y-1.5 max-h-72 overflow-y-auto">
                {/* Opção: sem cliente */}
                <label
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                    selectedId === null
                      ? 'bg-indigo-50 border-indigo-300'
                      : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="customer"
                    checked={selectedId === null}
                    onChange={() => setSelectedId(null)}
                    className="accent-indigo-600"
                  />
                  <span className="text-sm text-gray-700 italic">— Sem cliente (pessoal) —</span>
                </label>

                {/* Lista de customers */}
                {customers.map((c) => (
                  <label
                    key={c.id}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                      selectedId === c.id
                        ? 'border-indigo-300'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                    style={selectedId === c.id ? {
                      background: (c.color ?? '#6366f1') + '12',
                      borderColor: (c.color ?? '#6366f1') + '80',
                    } : undefined}
                  >
                    <input
                      type="radio"
                      name="customer"
                      checked={selectedId === c.id}
                      onChange={() => setSelectedId(c.id)}
                      className="accent-indigo-600"
                    />
                    <span className="text-lg">{c.icon ?? '👤'}</span>
                    <span className="text-sm font-medium text-gray-800 flex-1 truncate">{c.name}</span>
                    {c.color && (
                      <span className="w-3 h-3 rounded-full shrink-0" style={{ background: c.color }} />
                    )}
                  </label>
                ))}

                {/* Botão criar novo cliente */}
                <button
                  type="button"
                  onClick={() => setCreatingNew(true)}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-indigo-300 text-indigo-700 hover:bg-indigo-50 text-sm font-medium transition-colors"
                >
                  <Plus size={14} /> Criar novo cliente
                </button>
              </div>
            )}

            {err && (
              <div className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700">
                {err}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !dirty}
              className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed rounded-lg inline-flex items-center gap-1.5"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              Salvar
            </button>
          </div>
        </div>
      </div>

      {creatingNew && (
        <CustomerModal
          customer={null}
          onClose={() => setCreatingNew(false)}
          onSaved={async (created) => {
            setCreatingNew(false);
            await refresh();
            if (created?.id) setSelectedId(created.id);
          }}
        />
      )}
    </>
  );
}
